import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import './_sticky-header.scss';
import { LayoutContext } from '../layout-context-container';

function StickyHeader({children}) {
  const {cookieStored, hcpSubnavState, setStickyHeaderFixedState, showMobileMenu, jumpSectionData, autoScrollActive} = useContext(LayoutContext);
  const stickyHeaderRef = useRef(null);
  const wrapperRef = useRef(null);
  const [menuFixed, _setMenuFixed] = useState(false);
  const _menuFixed = useRef(menuFixed);
  const setMenuFixed = (/** @type {boolean} */ val) => {
    _setMenuFixed(val);
    _menuFixed.current = val;
  }
  const [positionFixedReady, _setPositionFixedReady] = useState(false);
  const _positionFixedReady = useRef(positionFixedReady); 
  const setPositionFixedReady = (/** @type {boolean} */ val) => {
    _setPositionFixedReady(val);
    _positionFixedReady.current = val;
  }
  const [lastHeight, _setLastHeight] = useState(0);
  const _lastHeight = useRef(lastHeight);
  const setLastHeight = (/** @type {Number} */ val) => {
    _setLastHeight(val);
    _lastHeight.current = val;
  }

  useEffect(() => {
    // handle sticky header show/hide logic
    let currentYOffset = 0;
    let travel = 0;

    //// adjust saved offset if the subnav is opened to prevent the menu from going away ///////
    const headerRef = stickyHeaderRef.current;
    function handleNavClick(){
      const hasSubNavSpacing = stickyHeaderRef.current?.classList.contains("subnav-spacing");
      if (!hasSubNavSpacing){
        currentYOffset += 59;
      }
    }
    if(stickyHeaderRef.current?.parentElement.classList.contains("hcp-layout")){
      headerRef?.addEventListener('click', handleNavClick);
    }

    function handleScroll() {
      const offsetY = window.scrollY;
      // @ts-ignore
      const headerHeight = wrapperRef.current !== undefined ? wrapperRef.current.getBoundingClientRect().height : 0;

      if (headerHeight >= 0) {
        setLastHeight(headerHeight);
      }

      if (currentYOffset >= offsetY && offsetY >= headerHeight && !_menuFixed.current && !autoScrollActive) {

        if (!_menuFixed.current && travel >= 10) {
          // show header after scrolling 10 pixels
          setMenuFixed(true);
        } else {
          travel++;
        }
      } else if (currentYOffset < offsetY && _menuFixed.current) {
        setMenuFixed(false);
        travel = 0;
      }

      if (offsetY <= 0) {
        setMenuFixed(false);
        travel = 0;
      }

      if (offsetY >= headerHeight && !_positionFixedReady.current && travel >= 10) {
        setPositionFixedReady(true);
      } else if (offsetY < headerHeight) {
        setPositionFixedReady(false);
      }

      currentYOffset = offsetY;
    }
    
    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('scroll',handleScroll);
      headerRef?.removeEventListener('click', handleNavClick);
    }
  }, [autoScrollActive]);

  useEffect(() => {
    function handleResize() {
      // reset height
      // @ts-ignore
      const headerHeight = wrapperRef.current.getBoundingClientRect().height;

      if (headerHeight >= 0) {
        setLastHeight(headerHeight);
      }
    }

    window.addEventListener('resize', handleResize);

    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    // init header height when rendered
    if (wrapperRef.current !== undefined || cookieStored) {
      // @ts-ignore
      const headerHeight = wrapperRef.current.getBoundingClientRect().height;

      if (headerHeight >= 0) {
        setLastHeight(Math.round(headerHeight));
      }
    }
  }, [wrapperRef, cookieStored, hcpSubnavState, jumpSectionData]);

  useEffect(() => {
    // set header height to create space for position fixed
    if (lastHeight >= 0) {
      // @ts-ignore
      stickyHeaderRef.current?.style.setProperty('height', `${lastHeight}px`);

      const el = stickyHeaderRef.current?.querySelector('.patient-jump-to-section--sticky');
      // @ts-ignore
      stickyHeaderRef.current?.style.setProperty('--sticky-header-top', `-${el ? lastHeight + 90 : lastHeight}px`);
    }

    setStickyHeaderFixedState(menuFixed);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [menuFixed, lastHeight, stickyHeaderRef, wrapperRef, cookieStored, jumpSectionData]);

  useEffect(() => {
    if (showMobileMenu) {
      wrapperRef.current?.scrollTo({top: 0, behavior: "instant"})
    } else {
      const headerHeight = wrapperRef.current.getBoundingClientRect().height;

      if (headerHeight >= 0) {
        setLastHeight(headerHeight);
      }
    }
  }, [showMobileMenu]);

  const containerClass = useMemo(() => {
    var className = 'sticky-header';

    /////// keep subnav padding if it was already applied //////
    // !!! this class is also applied in components-hcp/header/components/header-nav/index.js
    const hasSubNavSpacing = stickyHeaderRef.current?.classList.contains("subnav-spacing");
    if(hasSubNavSpacing){
      className = `${className} subnav-spacing`;
    }

    if (menuFixed) {
      className = `${className} sticky-header--fixed`;
    }

    if (positionFixedReady) {
      className = `${className} sticky-header--ready`;
    }

    return className;
  }, [menuFixed, positionFixedReady]);

  return (
    <div className={containerClass} ref={stickyHeaderRef}>
      <div className="sticky-header__wrapper" ref={wrapperRef}>
        {children}
        <div className="sticky-header__header-nav-space" />
      </div>
    </div>
  );
}

export default StickyHeader;